/**
 * Main Layout Component - Eliza Forge
 *
 * Three-pane layout: Header + Navigation + Main Content + Optional Right Panel
 * Following the Linear-style design system from UX specification.
 *
 * Uses AppSidebar for unified navigation with animated content switching.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import AppSidebar from './AppSidebar';
import TenantViewBanner from './TenantViewBanner';
import LoginBanner from './LoginBanner';
import MultiTenantWelcome from './MultiTenantWelcome';
import DeactivatedTenantBanner from './DeactivatedTenantBanner';
import { useAuth } from '../../stores/useAuth';
import type { SidebarMode } from '../ui/sidebar';
import type { BreadcrumbTrailItem } from '../ui/breadcrumb';
import { cn } from '../../shared/lib/cn';
import { getRouteBreadcrumbs } from './routeBreadcrumbs';
import { useNavigationReturnTarget } from './navigationHistory';
import type { ToolbarSurface } from '../ui/toolbar';

const SIDEBAR_MODE_KEY = 'app_sidebar_mode';

function getPersistedSidebarMode(): SidebarMode {
  try {
    const raw = localStorage.getItem(SIDEBAR_MODE_KEY);
    if (raw === 'collapsed' || raw === 'expanded') return raw;
  } catch {
    // localStorage may be unavailable
  }
  return 'expanded';
}

function persistSidebarMode(mode: SidebarMode) {
  try {
    localStorage.setItem(SIDEBAR_MODE_KEY, mode);
  } catch {
    // localStorage may be unavailable
  }
}

interface LayoutProps {
  children: ReactNode;
  rightPanel?: ReactNode;
  showRightPanel?: boolean;
  pageTitle?: string;
  breadcrumbs?: BreadcrumbTrailItem[];
  /** Override sidebar mode */
  sidebarModeOverride?: SidebarMode;
  /** @deprecated Use sidebarModeOverride instead */
  sidebarCollapsedOverride?: boolean;
}

export function Layout({
  children,
  rightPanel,
  showRightPanel = false,
  pageTitle,
  breadcrumbs,
  sidebarModeOverride,
  sidebarCollapsedOverride,
}: LayoutProps) {
  const location = useLocation();
  const [sidebarMode, _setSidebarMode] = useState<SidebarMode>(getPersistedSidebarMode);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [contentToolbarSurface, setContentToolbarSurface] = useState<ToolbarSurface | undefined>();
  const [homeScrolled, setHomeScrolled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [tenantDeactivated, setTenantDeactivated] = useState<{
    tenantName: string;
    deactivatedAt?: string;
    message?: string;
  } | null>(null);
  const { user } = useAuth();

  const headerBreadcrumbs = useMemo(() => {
    if (breadcrumbs && breadcrumbs.length > 0) {
      return breadcrumbs;
    }
    return getRouteBreadcrumbs(location);
  }, [breadcrumbs, location]);
  const returnTarget = useNavigationReturnTarget(location, headerBreadcrumbs);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) {
      setContentToolbarSurface(undefined);
      return undefined;
    }

    const syncToolbarSurface = () => {
      const marker = root.querySelector<HTMLElement>('[data-toolbar-surface]');
      const nextSurface = marker?.dataset.toolbarSurface === 'solid' ? 'solid' : undefined;
      setContentToolbarSurface(current => (current === nextSurface ? current : nextSurface));
    };

    syncToolbarSurface();

    if (typeof MutationObserver === 'undefined') {
      return undefined;
    }

    const observer = new MutationObserver(syncToolbarSurface);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-toolbar-surface'],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [location.key, location.pathname]);

  useEffect(() => {
    const root = contentRef.current;
    const scrollContainer = root?.querySelector<HTMLElement>('[data-toolbar-scroll-container]');

    if (!scrollContainer || location.pathname !== '/home') {
      setHomeScrolled(false);
      return undefined;
    }

    const syncHomeScrolled = () => {
      setHomeScrolled(scrollContainer.scrollTop > 4);
    };

    syncHomeScrolled();
    scrollContainer.addEventListener('scroll', syncHomeScrolled, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', syncHomeScrolled);
  }, [location.key, location.pathname]);

  // Wrap setter to also persist to localStorage
  const setSidebarMode = useCallback((mode: SidebarMode) => {
    _setSidebarMode(mode);
    persistSidebarMode(mode);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const syncViewport = () => setIsNarrowViewport(mediaQuery.matches);
    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    if (!isNarrowViewport) {
      setMobileSidebarOpen(false);
    }
  }, [isNarrowViewport]);

  // Determine current sidebar mode (override takes precedence)
  const currentSidebarMode: SidebarMode = sidebarModeOverride
    ?? (sidebarCollapsedOverride !== undefined
      ? (sidebarCollapsedOverride ? 'collapsed' : 'expanded')
      : sidebarMode);
  const effectiveSidebarMode: SidebarMode = isNarrowViewport
    ? (mobileSidebarOpen ? 'expanded' : 'hidden')
    : currentSidebarMode;

  // Simple toggle - always the same behavior
  const toggleSidebar = () => {
    if (isNarrowViewport) {
      setMobileSidebarOpen(open => !open);
      return;
    }
    const newMode = sidebarMode === 'expanded' ? 'collapsed' : 'expanded';
    setSidebarMode(newMode);
  };

  const handleSidebarModeChange = useCallback((mode: SidebarMode) => {
    if (isNarrowViewport) {
      setMobileSidebarOpen(mode !== 'hidden');
      return;
    }
    setSidebarMode(mode);
  }, [isNarrowViewport, setSidebarMode]);

  // Listen for tenant deactivation events
  useEffect(() => {
    // Check if there's stored deactivation info on mount
    const storedDeactivation = localStorage.getItem('tenant_deactivated');
    if (storedDeactivation) {
      try {
        setTenantDeactivated(JSON.parse(storedDeactivation));
      } catch {
        // Ignore parsing errors
      }
    }

    // Listen for new deactivation events
    const handleTenantDeactivated = (event: CustomEvent) => {
      setTenantDeactivated({
        tenantName: event.detail.tenant_name,
        deactivatedAt: event.detail.deactivated_at,
        message: event.detail.message
      });
    };

    window.addEventListener('tenant-deactivated', handleTenantDeactivated as EventListener);

    return () => {
      window.removeEventListener('tenant-deactivated', handleTenantDeactivated as EventListener);
    };
  }, []);

  // Handle switching tenant when deactivated
  const handleSwitchTenant = () => {
    // Clear the deactivation state and info
    localStorage.removeItem('tenant_deactivated');
    setTenantDeactivated(null);
    // Tenant switching is available from the user menu.
    window.location.reload();
  };

  // If tenant is deactivated, show the full-screen banner
  if (tenantDeactivated) {
    return (
      <DeactivatedTenantBanner
        tenantName={tenantDeactivated.tenantName}
        deactivatedAt={tenantDeactivated.deactivatedAt}
        hasOtherTenants={user?.has_multiple_tenants}
        onSwitchTenant={handleSwitchTenant}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-dark-bg overflow-hidden">
      {/* Tenant View Banner - Shows when platform admin is viewing as tenant */}
      <TenantViewBanner />

      {/* Login Banner - Shows after login, auto-dismisses after 3 seconds */}
      <LoginBanner />

      {/* Multi-Tenant Welcome Popup - Shows for first-time multi-tenant users */}
      <MultiTenantWelcome />

      {/* Main Layout: Sidebar + (Toolbar + Content) - Gemini-style */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {isNarrowViewport && mobileSidebarOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* AppSidebar - Unified navigation with animated content switching */}
        <div
          className={cn(
            'flex-none',
            isNarrowViewport && 'fixed inset-y-0 left-0 z-50 md:static',
          )}
        >
          <AppSidebar
            mode={effectiveSidebarMode}
            onToggle={toggleSidebar}
            onModeChange={handleSidebarModeChange}
          />
        </div>

        {/* Right Column: Toolbar + Content */}
        <div className="relative flex-1 flex flex-col min-h-0 min-w-0">
          {/* Header/Toolbar - only spans content area */}
          <Header
            onMenuToggle={toggleSidebar}
            breadcrumbs={headerBreadcrumbs}
            returnTarget={returnTarget}
            surfaceOverride={contentToolbarSurface}
            homeScrolled={homeScrolled}
          />

          {/* Main Content */}
          <main className="flex-1 flex flex-col overflow-hidden min-w-0 bg-gray-50 dark:bg-dark-bg">
          {/* Page Header */}
          {pageTitle && (
            <div className="px-6 py-3">
              {/* Page Title */}
              <h1 className="text-2xl font-semibold text-text">
                {pageTitle}
              </h1>
            </div>
          )}

          {/* Page Content */}
          <div className="flex-1 overflow-hidden min-h-0 min-w-0">
            <div className={`flex h-full min-h-0 min-w-0 overflow-hidden ${showRightPanel ? '' : ''}`}>
              {/* Main content area */}
              <div ref={contentRef} className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
                {children}
              </div>

              {/* Right Panel */}
              {showRightPanel && rightPanel && (
                <div className="w-80 bg-surface-2 border-l border-border p-6 overflow-y-auto">
                  {rightPanel}
                </div>
              )}
            </div>
          </div>
        </main>

        </div>
      </div>
    </div>
  );
}

// Specialized layout components for common patterns

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function DashboardLayout({ children, title, subtitle, actions }: DashboardLayoutProps) {
  return (
    <Layout>
      <div className="space-y-6">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text">{title}</h1>
            {subtitle && (
              <p className="text-muted mt-1">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center space-x-3">
              {actions}
            </div>
          )}
        </div>

        {/* Dashboard Content */}
        {children}
      </div>
    </Layout>
  );
}

interface FormLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
  actions?: ReactNode;
}

export function FormLayout({ children, title, description, breadcrumbs, actions }: FormLayoutProps) {
  return (
    <Layout pageTitle={title} breadcrumbs={breadcrumbs}>
      <div className="max-w-4xl mx-auto">
        {/* Form Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-text">{title}</h1>
          {description && (
            <p className="text-muted mt-2">{description}</p>
          )}
        </div>

        {/* Form Content */}
        <div className="bg-surface rounded-lg border border-border p-6">
          {children}
        </div>

        {/* Form Actions */}
        {actions && (
          <div className="mt-6 flex justify-end space-x-3">
            {actions}
          </div>
        )}
      </div>
    </Layout>
  );
}

interface DetailLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
  actions?: ReactNode;
  rightPanel?: ReactNode;
  tabs?: Array<{ label: string; value: string; current: boolean }>;
  onTabChange?: (tab: string) => void;
}

export function DetailLayout({
  children,
  title,
  subtitle,
  breadcrumbs,
  actions,
  rightPanel,
  tabs,
  onTabChange
}: DetailLayoutProps) {
  return (
    <Layout
      breadcrumbs={breadcrumbs}
      rightPanel={rightPanel}
      showRightPanel={!!rightPanel}
    >
      <div className="space-y-6">
        {/* Detail Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-text">{title}</h1>
            {subtitle && (
              <p className="text-muted mt-1">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center space-x-3">
              {actions}
            </div>
          )}
        </div>

        {/* Tabs */}
        {tabs && tabs.length > 0 && (
          <div className="border-b border-border">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => onTabChange?.(tab.value)}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-fast
                    ${tab.current
                      ? 'border-brand text-brand'
                      : 'border-transparent text-muted hover:text-text hover:border-border-strong'
                    }
                  `}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Detail Content */}
        {children}
      </div>
    </Layout>
  );
}

export default Layout;
